import { action, computed, observable, set } from 'mobx';
import type { RootStore } from '~/stores/root.store';
import { IStyles } from '~/types/block-styles.types';
import type { TBlockType } from '~/types/model.types';
import { IBaseBlockResponse, TBlockResponse } from '~/types/server.types';
import { cloneClass } from '~/utils/data.utils';
import { DnDTypes, TBlock } from '~/types/builder.types';
import type { FlowRevision } from '~/models/flow-revision.model';
import { SHORT_HEIGHT_BLOCK_TYPES } from '~/common/flows.constants';
import type { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import type { ContainerBlock } from '~/models/blocks/container-block.model';
import type { CompanyInfoBlock } from '~/models/blocks/company-info-block.model';
import type { LeadDetailsBlock } from '~/models/blocks/lead-details-block.model';
import type { QuestionBlock } from '~/models/blocks/question-block.model';
import { ACTION_TYPE_TO_SOURCE_TYPE_MAP } from '~/utils/block.utils';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { ImageBlock } from '~/models/blocks/image-block.model';

export const BLOCK_TYPE_TO_LABEL: Record<TBlockType, string> = {
    charge_block: 'Payment',
    charge_container_block: 'Payment',
    company_info_block: 'Company info',
    container_block: 'Content',
    contract_block: 'Contract',
    contract_html_block: 'Content',
    iframe_block: 'IFrame',
    image_block: 'Image',
    invoice_column_header_block: 'Invoice column header',
    invoice_container_block: 'Invoice',
    invoice_header_block: 'Invoice header',
    invoice_service_item_block: 'Service sub-item',
    invoice_summary_block: 'Invoice summary',
    lead_details_block: 'Contact info',
    payment_block: 'Payment',
    payment_schedule_block: 'Payment schedule',
    question_block: 'Question',
    recurring_schedule_block: 'Payment schedule',
    schedule_container_block: 'Scheduler',
    schedule_session_block: 'Session',
    service_items_container_block: null,
    service_selection_block: 'Services',
    service_selection_item_block: 'Service item',
    services_block: 'Invoice items',
    signature_block: 'Signature',
    signatures_block: 'Signature',
    social_block: 'Social',
    spacer_block: 'Divider',
    text_block: 'Text',
    video_block: 'Video'
};

export type BaseBlockDataForServer = Pick<
    IBaseBlockResponse,
    'flow_id' | 'page_id' | 'block_id' | 'styles'
> & {
    source_type?: (typeof ACTION_TYPE_TO_SOURCE_TYPE_MAP)[keyof typeof ACTION_TYPE_TO_SOURCE_TYPE_MAP];
};

export abstract class BaseBlock {
    abstract type: TBlockType;
    @observable block_id: string;
    page_id: string;
    @observable flow_id: string;
    @observable flowRevision: FlowRevision<RootStore>;
    @observable parent_id: string;
    @observable styles: IStyles = {};
    @observable connected_variable_id: string;
    @observable critical_changes: Array<string>;
    resizeable: boolean;
    rootStore: RootStore;
    initialAttributes: any;

    get label() {
        return BLOCK_TYPE_TO_LABEL[this.type];
    }

    constructor(
        block: IBaseBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore,
        initialAttributes?: any
    ) {
        Object.assign(this, block);
        this.styles = block.styles || {};
        this.resizeable = false;
        this.critical_changes = block.critical_changes || [];
        this.flowRevision = flowRevision;
        this.rootStore = rootStore;
        this.initialAttributes = initialAttributes;
    }

    get isPrintable() {
        return true;
    }

    @action
    onBlockCreated() {
        return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hasFocusedChild(selected: boolean) {
        return false;
    }

    @computed
    get isRootBlock() {
        return this.block_id === this.page.root_block_id;
    }

    isContainerBlock(): this is ContainerBlock {
        return false;
    }

    @computed
    get isRootChild() {
        return this.parent && this.parent.isRootBlock;
    }

    get sidebarContextBlock(): any {
        return this;
    }

    @computed
    get attributable() {
        return this.isRootChild || (this.page && this.isRootBlock);
    }

    @computed
    get isInsideInvoiceBlock(): boolean {
        let { parent } = this;
        while (parent) {
            if (
                this.isInvoiceContainerBlock ||
                parent.isInvoiceContainerBlock
            ) {
                return true;
            }
            parent = parent?.parent;
        }
        return false;
    }

    @computed
    get hoverable() {
        return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isDraggable(customProps?: any) {
        return !this.isInsideLinkedCompanyInfoBlockInTemplate;
    }

    @computed
    get dndType(): DnDTypes {
        return DnDTypes.Block;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dndAcceptedTypes(additionalProps?: any): DnDTypes | DnDTypes[] | null {
        return this.dndType;
    }

    @computed
    get parent(): ContainerableBlock {
        return <ContainerableBlock>this.flowRevision.blocksMap[this.parent_id];
    }

    currentSize() {}

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    canBeParentOf(block: TBlock) {
        return false;
    }

    @computed
    get order() {
        const { parent } = this;

        if (!parent) {
            return 0;
        }

        return parent.children.find(x => x.block_id === this.block_id)?.order;
    }

    // Gets the first parent that is a root level block
    calculateRootLevelParent() {
        if (this.isRootChild) {
            return null;
        }

        let { parent } = this;

        while (parent && !parent.isRootChild) {
            parent = parent.parent;
        }
        return parent;
    }

    @computed
    get connectedVariable() {
        return this.flowRevision.variablesMap[this.connected_variable_id];
    }

    @computed
    get flow() {
        return this.rootStore.flowStore.flows[this.flow_id];
    }

    @computed
    get page() {
        return this.flowRevision.pagesMap[this.page_id];
    }

    @action
    setStyles(styles: IStyles) {
        // TODO: this is a temp method
        set(this.styles, styles);
    }

    @action
    mergeBlockAttributes(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        block: TBlockResponse,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        attributesToMerge: Record<string, any>
    ) {
        this.critical_changes = block.critical_changes || [];
    }

    @action
    setFlowRevision(flowRevision: FlowRevision<RootStore>) {
        this.flowRevision = flowRevision;
    }

    @action
    setFlowId(id: string) {
        this.flow_id = id;
    }

    get errors() {
        return {};
    }

    get blockInputValidations() {
        return {};
    }

    get isActionBlock() {
        return false;
    }

    get shouldAddCompositionMenu() {
        return false;
    }

    get isResponsesActionBlock() {
        return false;
    }

    isQuestionBlock(): this is QuestionBlock {
        return this.type === 'question_block';
    }

    isCanvaImageBlock() {
        if (this.type === 'image_block') {
            return (this as unknown as ImageBlock).isCanvaBlock;
        }

        return false;
    }

    get isRegularQuestionBlock(): boolean {
        return Boolean(this.isQuestionBlock() && !this.isLeadDetailsQuestion);
    }

    isLeadDetailsBlock(): this is LeadDetailsBlock {
        return this.type === 'lead_details_block';
    }

    get isInvoiceSummaryBlock() {
        return this.type === 'invoice_summary_block';
    }

    get isInvoiceServiceItemBlock() {
        return this.type === 'invoice_service_item_block';
    }

    @computed
    get isServiceItemBlock() {
        return (
            this.isServiceSelectionItemBlock || this.isInvoiceServiceItemBlock
        );
    }

    get isSignaturesBlock() {
        return this.type === 'signatures_block';
    }

    get isSignatureBlock() {
        return this.type === 'signature_block';
    }

    get isInvoiceContainerBlock() {
        return this.type === 'invoice_container_block';
    }

    get isInvoiceHeaderBlock() {
        return this.type === 'invoice_header_block';
    }

    get isServiceSelectionBlock() {
        return this.type === 'service_selection_block';
    }

    get isServiceSelectionItemBlock() {
        return this.type === 'service_selection_item_block';
    }

    get isTextBlock() {
        return this.type === 'text_block';
    }

    get isPayContainerBlock() {
        return this.type === 'charge_container_block';
    }

    @computed
    get isInvoiceOrPayBlock() {
        return this.isInvoiceContainerBlock || this.isPayContainerBlock;
    }

    get isInvoiceOrPay() {
        return this.isInvoiceOrPayBlock;
    }

    get isSessionSelection() {
        return this.isSessionSelectionBlock;
    }

    get isShortHeightBlock() {
        return SHORT_HEIGHT_BLOCK_TYPES.includes(this.type);
    }

    get isImageBlock() {
        return this.type === 'image_block';
    }

    get isPaymentScheduleBlock() {
        return this.type === 'payment_schedule_block';
    }

    get isRecurringScheduleBlock() {
        return this.type === 'recurring_schedule_block';
    }

    get isPaymentBlock() {
        return this.type === 'payment_block';
    }

    get isServicesBlock() {
        return this.type === 'services_block';
    }

    get isInvoiceColumnHeaderBlock() {
        return this.type === 'invoice_column_header_block';
    }

    get isContractBlock() {
        return this.type === 'contract_block';
    }

    get isContractHtmlBlock() {
        return this.type === 'contract_html_block';
    }

    get isChargeBlock() {
        return this.type === 'charge_block';
    }

    get isChargeContainerBlock() {
        return this.type === 'charge_container_block';
    }

    get isServiceItemsContainerBlock() {
        return this.type === 'service_items_container_block';
    }

    get isSessionSelectionBlock() {
        if (this.isServiceSelectionBlock) {
            return (this as unknown as ServiceSelectionBlock)
                .isSessionSelection;
        }
        return false;
    }

    get isScheduleContainerBlock() {
        return this.type === 'schedule_container_block';
    }

    get isScheduleSessionBlock() {
        return this.type === 'schedule_session_block';
    }

    get isVideoBlock() {
        return this.type === 'video_block';
    }

    get displayName() {
        return this.type.toString();
    }

    @computed
    get isSelfSelected() {
        const { selectedBlockId } = this.rootStore.builderUiStore;
        return this.block_id === selectedBlockId;
    }

    @computed
    get criticalChanges() {
        return this.critical_changes || [];
    }

    @computed
    get hasCriticalChanges() {
        return this.critical_changes?.length > 0;
    }

    @computed
    get shouldExpandOnSelect() {
        return true;
    }

    @computed
    get cogIconTestId() {
        return undefined;
    }

    get isInsideLinkedCompanyInfoBlockInTemplate() {
        return !!(this.calculateRootLevelParent() as CompanyInfoBlock)
            ?.isLinkedInTemplate;
    }

    @computed
    get shouldHidePlus() {
        return this.isInsideLinkedCompanyInfoBlockInTemplate;
    }

    abstract get isDeletable(): boolean;

    @computed
    get isDeleteDisabled() {
        return this.isInsideLinkedCompanyInfoBlockInTemplate;
    }

    @computed
    get isDragDisabled() {
        return this.isInsideLinkedCompanyInfoBlockInTemplate;
    }

    @computed
    get isCutDisabled() {
        return false;
    }

    @computed
    get isImageReplaceDisabled() {
        return this.isInsideLinkedCompanyInfoBlockInTemplate;
    }

    private static get tooltipMap() {
        return {
            linkedToCompanyInfo:
                'Unlink this block in the right side bar in order to edit it.'
        };
    }

    @computed
    get deleteTooltip() {
        if (this.isInsideLinkedCompanyInfoBlockInTemplate) {
            return BaseBlock.tooltipMap.linkedToCompanyInfo;
        }
        return null;
    }

    @computed
    get deleteDisabledTooltip() {
        return null;
    }

    @computed
    get dragDisabledTooltip() {
        if (this.isInsideLinkedCompanyInfoBlockInTemplate) {
            return BaseBlock.tooltipMap.linkedToCompanyInfo;
        }
        return null;
    }

    @computed
    get imageReplaceDisabledTooltip() {
        if (this.isInsideLinkedCompanyInfoBlockInTemplate) {
            return BaseBlock.tooltipMap.linkedToCompanyInfo;
        }
        return null;
    }

    get coupledBlocks(): TBlock[] {
        return [];
    }

    @computed
    get index() {
        return this.parent?.childBlocks.findIndex(
            block => block.block_id === this.block_id
        );
    }

    @computed
    get displayOrder() {
        return this.index + 1;
    }

    afterActionsApplied() {}

    // TODO: https://honeybook.atlassian.net/browse/FLOW-532
    getDataForServer(): BaseBlockDataForServer {
        return {
            flow_id: this.flow_id,
            page_id: this.page_id,
            block_id: this.block_id,
            styles: { ...this.styles }
        };
    }

    clone() {
        return cloneClass(this);
    }
}
